import { assignTaskToFileThunk } from '../../../redux/thunks/file';
import { BoardTaskObject } from '../../../params/redux/task';
import CustomInput from '../../reusable/input';
import CustomModal from '../../modals';
import { Search } from '@mui/icons-material';
import { useParams } from 'react-router';
import { AppDispatch, RootState } from '../../../redux/store';
import { Box, Checkbox, CircularProgress, InputAdornment, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useMemo, useState } from 'react';

/**
 * @heading1: TaskSelectorModal
 * @paragraph: A modal component for adding a new message board. Uses Formik for form handling
 * and integrates with Redux for message board creation.
 * @param: {boolean} open - Whether the modal is open or not.
 * @param: {function} onClose - Function to close the modal.
 */

const TaskSelectorModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
    /**
     * @heading3: Variables
     * @ol:
     * dispatch: {AppDispatch} - The Redux dispatch function to trigger actions.
     * loaders: An object containing the loader details from the Redux store.
     */
    const dispatch = useDispatch<AppDispatch>();
    const { id } = useParams<{ id: string }>();
    const { loaders } = useSelector((state: RootState) => state.messageboard);
    const { tasks } = useSelector((state: RootState) => state.task);
    const { selectedFileDetail } = useSelector((state: RootState) => state.file);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [loadingTaskId, setLoadingTaskId] = useState<number | null>(null);

    const allTasks: BoardTaskObject[] = useMemo(() => {
        return tasks?.lists.flatMap((list) => list.items.map((item) => ({
            ...item,
            list_name: list.list_name
        }))) || [];
    }, [tasks]);

    const filteredTasks = useMemo(() => {
        const lowerSearch = searchTerm.toLowerCase();
        return allTasks.filter((task) => task?.title?.toLowerCase().includes(lowerSearch) || task?.description?.toLowerCase().includes(lowerSearch));
    }, [searchTerm, allTasks]);

    /**
     * @heading3: Functions
     * @heading4: Handles the form submission by dispatching a Redux thunk.
     * @param: {PipelineParams} values - The form values for creating a message board.
     */
    const handleSelectTask = async (task: BoardTaskObject) => {
        const isSelected = selectedIds.includes(task.id);

        const updatedSelected = isSelected
            ? selectedIds.filter((task_id) => task_id !== task.id)
            : [...selectedIds, task.id];

        setSelectedIds(updatedSelected);
        setLoadingTaskId(task.id);

        try {
            await dispatch(assignTaskToFileThunk({ task_id: task.id, client_id: id ? parseInt(id as string, 10) : 0 })).unwrap();
        } catch (e) {
            //
        } finally {
            setLoadingTaskId(null);
        }
    };

    return <CustomModal open={open} handleClose={loaders.isCreating ? () => null : onClose} title="Assign Task">
        <Box display="flex" flexDirection="column" gap={1}>
            {/* Input field for message board name */}
            <CustomInput
                variant="outlined"
                placeholder="Search tasks..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                fullWidth
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <Search />
                        </InputAdornment>
                    )
                }}
            />
            <Box display="flex" flexDirection="column" gap={1} maxHeight="300px" overflow="auto">
                {filteredTasks.length > 0
                    ? (
                        filteredTasks
                            .filter((taskParent) => (
                                !selectedFileDetail?.assigned_tasks?.some((task) => task.id === taskParent.id)
                            ))
                            .map((task) => (
                                selectedFileDetail?.assigned_tasks?.some((r) => r.id === task.id)
                                    ? null
                                    : (
                                        <Box
                                            key={task.id}
                                            display="flex"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            p={1}
                                            border="1px solid #eee"
                                            borderRadius="6px"
                                        >
                                            <Box display="flex" flexDirection="column">
                                                <Typography variant="subtitle2">{task?.title}</Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    {task?.due_date} • {task?.title}
                                                </Typography>
                                                <Typography variant="body2" mt={0.5}>
                                                    {task?.description}
                                                </Typography>
                                            </Box>
                                            {/* Optional Checkbox or selection icon can go here */}
                                            {loadingTaskId === task.id
                                                ? (
                                                    <CircularProgress size={20} />
                                                )
                                                : (
                                                    <Checkbox
                                                        checked={selectedIds.includes(task.id)}
                                                        onChange={() => handleSelectTask(task)}
                                                    />
                                                )}
                                        </Box>
                                    )
                            ))
                    )
                    : (
                        <Typography variant="body2" color="textSecondary" textAlign="center">
                            No tasks found.
                        </Typography>
                    )}
            </Box>
        </Box>
    </CustomModal>;
};

export default TaskSelectorModal;